import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../shared/theme/ThemeContext';
import { useAuth } from '../user-dashboard/auth/AuthContext';
import {
  DEFAULT_OPTIONS,
  alreadyProcessed,
  apiError,
  boundsUrl,
  cancelJob,
  clearJobInput,
  createJob,
  createWorkspace,
  deleteJob,
  fetchJob,
  fetchJobs,
  fetchProfile,
  fetchShots,
  fetchUsage,
  fetchWorkspaces,
  inviteWorkspace,
  lookupWorkspaceAccount,
  formatBytes,
  infoUrl,
  parseCogInfo,
  renameWorkspace,
  startJob,
  availableDroneIndexes,
  droneIndexNeeds,
  isDroneIndex,
  tileUrl,
  type CogBandInfo,
  uploadSmallFile,
  uploadZip,
  type DroneFileRef,
  type DroneJob,
  type DroneOptions,
  type DroneRaster,
  type DroneProfile,
  type WorkspaceSummary,
  type WorkspaceUsage,
} from './api';

type Extent = [number, number, number, number];

export interface DroneJobOverlay {
  layers: { key: string; tileUrl: string }[];
  shots: { type: string; features: object[] } | null;
  focus: { key: string; extent: Extent } | null;
}

interface ShotCollection {
  type: 'FeatureCollection';
  features: object[];
}

const OUTPUTS: { id: string; label: string }[] = [
  { id: 'orthomosaic', label: 'Orthomosaic' },
  { id: 'flood_depth', label: 'Flood depth (FwDET-ready)' },
  { id: 'dsm', label: 'DSM' },
  { id: 'drought', label: 'Drought layer' },
  { id: 'dtm', label: 'DTM' },
  { id: 'cracks', label: 'Crack segmentation' },
  { id: 'flood_mask', label: 'Flood mask' },
  { id: 'plants', label: 'Plant count / affected plants' },
];

const STAGE_ROWS: { id: string; label: string }[] = [
  { id: 'upload', label: 'Upload complete' },
  { id: 'alignment', label: 'Image alignment' },
  { id: 'sparse', label: 'Sparse cloud generation' },
  { id: 'dense', label: 'Dense cloud generation' },
  { id: 'orthomosaic', label: 'Orthomosaic generation' },
  { id: 'export', label: 'Export products' },
];

interface LocalZip {
  file: File | null;
  url: string;
  progress: number | null;
  uploaded: boolean;
  startedAt: number | null;
  speedBps: number | null;
  measured: boolean;
}

interface DeletePrompt {
  title: string;
  body: string;
  confirmLabel: string;
  run: () => Promise<void> | void;
}

const EMPTY_ZIP: LocalZip = {
  file: null,
  url: '',
  progress: null,
  uploaded: false,
  startedAt: null,
  speedBps: null,
  measured: false,
};

/** Last speed seen in this tab, so the next file can show a time before the first chunk lands. */
let rememberedUploadBps: number | null = null;

function sampleUploadSpeed(
  startedAt: number,
  bytesSent: number,
  previous: number | null,
  alreadyMeasured: boolean
): { bps: number | null; measured: boolean } {
  const elapsed = (Date.now() - startedAt) / 1000;
  if (elapsed < 0.4 || bytesSent < 32 * 1024) return { bps: previous, measured: false };
  const instant = bytesSent / elapsed;
  const bps = alreadyMeasured && previous ? previous * 0.45 + instant * 0.55 : instant;
  rememberedUploadBps = bps;
  return { bps, measured: true };
}

function formatRate(bps: number): string {
  if (bps >= 1024 * 1024) return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
  if (bps >= 1024) return `${Math.max(1, Math.round(bps / 1024))} KB/s`;
  return `${Math.max(1, Math.round(bps))} B/s`;
}

function formatEta(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 8) return 'a few seconds';
  if (seconds < 60) return `${Math.round(seconds / 5) * 5} seconds`;
  const minutes = Math.round(seconds / 60);
  if (minutes <= 1) return '1 minute';
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
}

function formatAnalysis(seconds: number): string {
  const safe = Math.max(0, Math.round(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.round((safe % 3600) / 60);
  if (hours && minutes) return `${hours} h ${minutes} min`;
  if (hours) return `${hours} h`;
  if (minutes) return `${minutes} min`;
  return '0 min';
}

function applyZipProgress(prev: LocalZip, file: File, fraction: number): LocalZip {
  const startedAt = prev.startedAt ?? Date.now();
  const sample = sampleUploadSpeed(startedAt, fraction * file.size, prev.speedBps, prev.measured);
  return {
    ...prev,
    progress: fraction,
    startedAt,
    speedBps: sample.bps ?? prev.speedBps,
    measured: prev.measured || sample.measured,
  };
}

function uploadTip(bytes: number, progress: number, speedBps: number | null, measured: boolean): string {
  if (progress >= 0.98) return 'Almost done. Finishing the upload.';
  if (!speedBps || speedBps <= 0) return 'Measuring your connection. Time left shows up in a moment.';
  const left = Math.max(0, bytes * (1 - progress));
  const time = formatEta(left / speedBps);
  const speed = formatRate(speedBps);
  if (!measured) return `Your recent upload speed is ${speed}, so this file should take about ${time}.`;
  return `Your upload speed is ${speed}, so about ${time} left.`;
}

function isActive(status: string | undefined): boolean {
  return status === 'preparing' || status === 'queued' || status === 'running';
}

interface BandSource {
  presigned: string;
  cacheKey: string;
  kind: DroneRaster;
}

function rasterFile(item: DroneJob, kind: DroneRaster): { presigned?: string; key: string | null } {
  if (kind === 'dsm') return { presigned: item.tiles.dsm, key: item.outputs.dsm_key };
  if (kind === 'dtm') return { presigned: item.tiles.dtm, key: item.outputs.dtm_key };
  return { presigned: item.tiles.orthophoto, key: item.outputs.orthophoto_key };
}

/** COG the map is drawing for this flight. Elevation layers only when that layer is selected. */
function bandSource(item: DroneJob, kind: DroneRaster): BandSource | null {
  const file = rasterFile(item, kind);
  if (!file.presigned || !file.key) return null;
  return { presigned: file.presigned, cacheKey: file.key, kind };
}

export default function DroneProcessingPanel({
  onOverlayChange,
  initialWorkspaceId = null,
}: {
  onOverlayChange?: (overlay: DroneJobOverlay | null) => void;
  initialWorkspaceId?: string | null;
}) {
  const [profile, setProfile] = useState<DroneProfile | null>(null);
  const [options, setOptions] = useState<DroneOptions>(DEFAULT_OPTIONS);
  const [rgb, setRgb] = useState<LocalZip>(EMPTY_ZIP);
  const [multispec, setMultispec] = useState<LocalZip>(EMPTY_ZIP);
  const [gcp, setGcp] = useState<File | null>(null);
  const [gcpProgress, setGcpProgress] = useState<number | null>(null);
  const [gcpUploaded, setGcpUploaded] = useState(false);
  const [gcpSpeed, setGcpSpeed] = useState<number | null>(null);
  const [gcpMeasured, setGcpMeasured] = useState(false);
  const [flightLog, setFlightLog] = useState<File | null>(null);
  const [logProgress, setLogProgress] = useState<number | null>(null);
  const [logUploaded, setLogUploaded] = useState(false);
  const [logSpeed, setLogSpeed] = useState<number | null>(null);
  const [logMeasured, setLogMeasured] = useState(false);
  const [job, setJob] = useState<DroneJob | null>(null);
  const [jobs, setJobs] = useState<DroneJob[]>([]);
  const [visibleIds, setVisibleIds] = useState<string[]>([]);
  const [extents, setExtents] = useState<Record<string, Extent>>({});
  const [focus, setFocus] = useState<{ jobId: string; n: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [layerKind, setLayerKind] = useState<DroneRaster>('ortho');
  const [layerIndex, setLayerIndex] = useState<{ a: number; b: number; colormap: string } | null>(null);
  const [shots, setShots] = useState<ShotCollection | null>(null);
  const [showCameras, setShowCameras] = useState(false);
  const [studioTab, setStudioTab] = useState<'flights' | 'new'>('new');
  const [shareOpen, setShareOpen] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState<WorkspaceUsage | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(initialWorkspaceId);
  const [workspacesReady, setWorkspacesReady] = useState(false);
  const [workspaceMenu, setWorkspaceMenu] = useState(false);
  const [naming, setNaming] = useState<'create' | 'rename' | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [workspaceBusy, setWorkspaceBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DeletePrompt | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [extras, setExtras] = useState({ gcp: false, log: false });
  const [advOpen, setAdvOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const pickedTab = useRef(false);
  const draftIdRef = useRef<string | null>(null);
  const draftPromise = useRef<Promise<string> | null>(null);
  const uploadChain = useRef<Promise<void>>(Promise.resolve());
  const rgbFileRef = useRef<File | null>(null);
  const msFileRef = useRef<File | null>(null);
  const gcpFileRef = useRef<File | null>(null);
  const logFileRef = useRef<File | null>(null);

  const zoomTo = useCallback((jobId: string) => {
    setFocus((prev) => ({ jobId, n: (prev?.n ?? 0) + 1 }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchProfile()
      .then((nextProfile) => {
        if (!cancelled) setProfile(nextProfile);
      })
      .catch((err) => {
        if (!cancelled) setError(apiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchWorkspaces()
      .then((list) => {
        if (cancelled) return;
        setWorkspaces(list);
        setActiveWorkspaceId((current) => {
          if (current && list.some((item) => item.workspace_id === current)) return current;
          return list[0]?.workspace_id ?? null;
        });
      })
      .catch((err) => {
        if (!cancelled) setError(apiError(err));
      })
      .finally(() => {
        if (!cancelled) setWorkspacesReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    draftIdRef.current = null;
    draftPromise.current = null;
    rgbFileRef.current = null;
    msFileRef.current = null;
    gcpFileRef.current = null;
    logFileRef.current = null;
    setRgb(EMPTY_ZIP);
    setMultispec(EMPTY_ZIP);
    setGcp(null);
    setGcpProgress(null);
    setGcpUploaded(false);
    setGcpSpeed(null);
    setGcpMeasured(false);
    setFlightLog(null);
    setLogProgress(null);
    setLogUploaded(false);
    setLogSpeed(null);
    setLogMeasured(false);
    if (!activeWorkspaceId) {
      setJobs([]);
      setJob(null);
      setVisibleIds([]);
      return;
    }
    let cancelled = false;
    fetchJobs(activeWorkspaceId)
      .then((list) => {
        if (cancelled) return;
        setJobs(list);
        const current = list.find((item) => isActive(item.status)) ?? list.find((item) => item.status === 'completed');
        setJob(current ?? null);
        setDetailId(null);
        if (current?.status === 'completed') {
          setVisibleIds([current.id]);
          zoomTo(current.id);
        } else {
          setVisibleIds([]);
        }
        setStudioTab(list.some((item) => item.status !== 'draft') ? 'flights' : 'new');
      })
      .catch((err) => {
        if (!cancelled) setError(apiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [activeWorkspaceId, zoomTo]);

  useEffect(() => {
    if (!job) return;
    setJobs((prev) => (prev.some((item) => item.id === job.id) ? prev.map((item) => (item.id === job.id ? job : item)) : [job, ...prev]));
  }, [job]);

  const lastStatus = useRef<string | null>(null);
  useEffect(() => {
    const now = job ? `${job.id}:${job.status}` : null;
    const was = lastStatus.current;
    lastStatus.current = now;
    if (!job || job.status !== 'completed' || !was?.startsWith(`${job.id}:`)) return;
    if (!isActive(was.slice(job.id.length + 1))) return;
    setVisibleIds((prev) => (prev.includes(job.id) ? prev : [...prev, job.id]));
    zoomTo(job.id);
  }, [job, zoomTo]);

  useEffect(() => {
    if (!job || !isActive(job.status)) return;
    const timer = window.setInterval(async () => {
      try {
        const next = await fetchJob(job.id);
        setJob(next);
      } catch (err) {
        setError(apiError(err));
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [job?.id, job?.status]);

  // Presigned urls change on every fetch; keep the first one per output so tiles don't reload.
  const tileCache = useRef(new Map<string, { tile: string; presigned: string }>());
  const tileFor = useCallback((item: DroneJob, kind: DroneRaster, index?: { a: number; b: number; colormap: string }) => {
    const { presigned, key: outputKey } = rasterFile(item, kind);
    if (!presigned || !outputKey) return null;
    if (isDroneIndex(kind) && !index) return null;
    const key =
      isDroneIndex(kind) && index
        ? `${item.id}:${kind}:${outputKey}:${index.a}:${index.b}`
        : `${item.id}:${kind}:${outputKey}`;
    let hit = tileCache.current.get(key);
    if (!hit) {
      hit = { tile: tileUrl(presigned, kind, index), presigned };
      tileCache.current.set(key, hit);
    }
    return { key, ...hit };
  }, []);

  const jobsById = useMemo(() => new Map(jobs.map((item) => [item.id, item])), [jobs]);

  const mapLayers = useMemo(
    () =>
      visibleIds.flatMap((id) => {
        const item = jobsById.get(id);
        if (!item || item.status !== 'completed') return [];
        const kind = id === job?.id ? layerKind : 'ortho';
        const index = id === job?.id && isDroneIndex(kind) ? layerIndex : null;
        const tile = isDroneIndex(kind) ? (index ? tileFor(item, kind, index) : null) : tileFor(item, kind);
        return tile ? [{ key: tile.key, tileUrl: tile.tile }] : [];
      }),
    [job?.id, jobsById, layerIndex, layerKind, tileFor, visibleIds]
  );

  const pendingBounds = useRef(new Set<string>());
  useEffect(() => {
    visibleIds.forEach((id) => {
      if (extents[id] || pendingBounds.current.has(id)) return;
      const item = jobsById.get(id);
      const tile = item ? tileFor(item, 'ortho') : null;
      if (!tile) return;
      pendingBounds.current.add(id);
      fetch(boundsUrl(tile.presigned))
        .then((response) => (response.ok ? response.json() : null))
        .then((body: { bounds?: number[] } | null) => {
          if (body?.bounds?.length === 4) setExtents((prev) => ({ ...prev, [id]: body.bounds as Extent }));
        })
        .catch(() => undefined)
        .finally(() => pendingBounds.current.delete(id));
    });
  }, [extents, jobsById, tileFor, visibleIds]);

  useEffect(() => {
    if (!job?.outputs.shots_key) {
      setShots(null);
      return;
    }
    let cancelled = false;
    fetchShots(job.id)
      .then((data) => {
        if (!cancelled) setShots(data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [job?.id, job?.outputs.shots_key]);

  const selectedVisible = Boolean(job && visibleIds.includes(job.id));
  const focusExtent = focus ? extents[focus.jobId] : undefined;
  const overlay = useMemo<DroneJobOverlay>(
    () => ({
      layers: mapLayers,
      shots: selectedVisible && showCameras ? shots : null,
      focus: focus && focusExtent ? { key: `${focus.jobId}:${focus.n}`, extent: focusExtent } : null,
    }),
    [focus, focusExtent, mapLayers, selectedVisible, showCameras, shots]
  );

  useEffect(() => {
    onOverlayChange?.(overlay);
  }, [onOverlayChange, overlay]);

  const selectJob = (item: DroneJob) => {
    if (detailId === item.id) {
      setDetailId(null);
      return;
    }
    setDetailId(item.id);
    setJob(item);
    setLayerKind('ortho');
    setLayerIndex(null);
    if (item.status === 'completed') {
      setVisibleIds((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
      zoomTo(item.id);
    }
  };

  const toggleVisible = (id: string) => {
    if (visibleIds.includes(id)) {
      setVisibleIds((prev) => prev.filter((value) => value !== id));
      return;
    }
    setVisibleIds((prev) => [...prev, id]);
    zoomTo(id);
  };

  useEffect(() => {
    return () => onOverlayChange?.(null);
  }, [onOverlayChange]);

  const disabledNote = profile?.disabled_note ?? 'Needs a larger server';
  const alignmentEnabled = profile?.enabled_alignment ?? ['low'];
  const skipDense = profile?.skip_dense !== false;

  const ensureDraft = useCallback(async () => {
    if (draftIdRef.current) return draftIdRef.current;
    if (!draftPromise.current) {
      if (!activeWorkspaceId) throw new Error('Create a workspace first.');
      draftPromise.current = createJob('Drone flight', options, activeWorkspaceId)
        .then((created) => {
          draftIdRef.current = created.id;
          setJob((current) => (current && isActive(current.status) ? current : created));
          return created.id;
        })
        .finally(() => {
          draftPromise.current = null;
        });
    }
    return draftPromise.current;
  }, [activeWorkspaceId, options]);

  const enqueueUpload = useCallback((task: () => Promise<void>) => {
    const run = uploadChain.current.then(task, task);
    uploadChain.current = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }, []);

  const stageZip = (role: 'rgb' | 'multispec', file: File) => {
    const fileRef = role === 'rgb' ? rgbFileRef : msFileRef;
    const setZip = role === 'rgb' ? setRgb : setMultispec;
    fileRef.current = file;
    const startedAt = Date.now();
    setZip({ ...EMPTY_ZIP, file, progress: 0, startedAt, speedBps: rememberedUploadBps });
    setError(null);
    void enqueueUpload(async () => {
      if (fileRef.current !== file) return;
      try {
        const id = await ensureDraft();
        if (fileRef.current !== file) return;
        await uploadZip(id, role, file, (fraction) => {
          if (fileRef.current !== file) return;
          setZip((prev) => (prev.file === file ? applyZipProgress(prev, file, fraction) : prev));
        });
        if (fileRef.current !== file) {
          await clearJobInput(id, role).catch(() => undefined);
          return;
        }
        setZip((prev) => (prev.file === file ? { ...prev, progress: 1, uploaded: true } : prev));
      } catch (err) {
        if (fileRef.current === file) {
          setZip((prev) => ({ ...prev, progress: null, uploaded: false }));
          setError(apiError(err));
        }
      }
    });
  };

  const clearZip = (role: 'rgb' | 'multispec') => {
    const fileRef = role === 'rgb' ? rgbFileRef : msFileRef;
    const setZip = role === 'rgb' ? setRgb : setMultispec;
    fileRef.current = null;
    setZip(EMPTY_ZIP);
    const id = draftIdRef.current;
    if (!id) return;
    void enqueueUpload(async () => {
      await clearJobInput(id, role).catch(() => undefined);
    });
  };

  const stageSmall = (role: 'gcp' | 'flight_log', file: File) => {
    const fileRef = role === 'gcp' ? gcpFileRef : logFileRef;
    const setFile = role === 'gcp' ? setGcp : setFlightLog;
    const setProgress = role === 'gcp' ? setGcpProgress : setLogProgress;
    const setUploaded = role === 'gcp' ? setGcpUploaded : setLogUploaded;
    const setSpeed = role === 'gcp' ? setGcpSpeed : setLogSpeed;
    const setMeasured = role === 'gcp' ? setGcpMeasured : setLogMeasured;
    const startedAt = Date.now();
    fileRef.current = file;
    setFile(file);
    setProgress(0);
    setUploaded(false);
    setSpeed(rememberedUploadBps);
    setMeasured(false);
    setError(null);
    void enqueueUpload(async () => {
      if (fileRef.current !== file) return;
      let latest = rememberedUploadBps;
      let measured = false;
      try {
        const id = await ensureDraft();
        if (fileRef.current !== file) return;
        await uploadSmallFile(id, role, file, (loaded, total) => {
          if (fileRef.current !== file) return;
          const sample = sampleUploadSpeed(startedAt, loaded, latest, measured);
          latest = sample.bps ?? latest;
          measured = measured || sample.measured;
          setProgress(total > 0 ? loaded / total : 0);
          setSpeed(latest);
          if (sample.measured) setMeasured(true);
        });
        if (fileRef.current !== file) {
          await clearJobInput(id, role).catch(() => undefined);
          return;
        }
        setProgress(1);
        setUploaded(true);
      } catch (err) {
        if (fileRef.current === file) {
          setProgress(null);
          setUploaded(false);
          setError(apiError(err));
        }
      }
    });
  };

  const clearSmall = (role: 'gcp' | 'flight_log') => {
    const fileRef = role === 'gcp' ? gcpFileRef : logFileRef;
    fileRef.current = null;
    if (role === 'gcp') {
      setGcp(null);
      setGcpProgress(null);
      setGcpUploaded(false);
      setGcpSpeed(null);
      setGcpMeasured(false);
    } else {
      setFlightLog(null);
      setLogProgress(null);
      setLogUploaded(false);
      setLogSpeed(null);
      setLogMeasured(false);
    }
    const id = draftIdRef.current;
    if (!id) return;
    void enqueueUpload(async () => {
      await clearJobInput(id, role).catch(() => undefined);
    });
  };

  const askRemoveZip = (role: 'rgb' | 'multispec') => {
    const value = role === 'rgb' ? rgb : multispec;
    const name = value.file?.name || value.url.trim().split('/').pop() || 'this file';
    setPendingDelete({
      title: 'Delete this file?',
      body: value.uploaded
        ? `“${name}” is already uploaded. Delete it if you do not want it processed. This removes the file and frees that space.`
        : `Delete “${name}”? It will not be processed.`,
      confirmLabel: 'Delete',
      run: () => clearZip(role),
    });
  };

  const askRemoveSmall = (role: 'gcp' | 'flight_log') => {
    const file = role === 'gcp' ? gcp : flightLog;
    const uploaded = role === 'gcp' ? gcpUploaded : logUploaded;
    const name = file?.name || 'this file';
    setPendingDelete({
      title: 'Delete this file?',
      body: uploaded
        ? `“${name}” is already uploaded. Delete it if you do not want it processed. This removes the file and frees that space.`
        : `Delete “${name}”? It will not be processed.`,
      confirmLabel: 'Delete',
      run: () => clearSmall(role),
    });
  };

  const onStart = useCallback(async () => {
    setError(null);
    if (!rgb.file && !multispec.file) {
      setError('Add an RGB or multispectral zip.');
      return;
    }
    if (!activeWorkspaceId) {
      setError('Create a workspace first.');
      return;
    }
    setBusy(true);
    try {
      const createdId = draftIdRef.current || (await ensureDraft());
      if (rgb.file && !rgb.uploaded) {
        await uploadZip(createdId, 'rgb', rgb.file, (fraction) =>
          setRgb((prev) => applyZipProgress(prev, rgb.file as File, fraction))
        );
        setRgb((prev) => ({ ...prev, progress: 1, uploaded: true }));
      }
      if (multispec.file && !multispec.uploaded) {
        await uploadZip(createdId, 'multispec', multispec.file, (fraction) =>
          setMultispec((prev) => applyZipProgress(prev, multispec.file as File, fraction))
        );
        setMultispec((prev) => ({ ...prev, progress: 1, uploaded: true }));
      }
      if (gcp && !gcpUploaded) await uploadSmallFile(createdId, 'gcp', gcp);
      if (flightLog && !logUploaded) await uploadSmallFile(createdId, 'flight_log', flightLog);
      let started;
      try {
        started = await startJob(createdId);
      } catch (err) {
        const again = alreadyProcessed(err);
        if (!again) throw err;
        if (!window.confirm(again.message)) return;
        started = await startJob(createdId, true);
      }
      draftIdRef.current = null;
      setJob(started);
      setRgb(EMPTY_ZIP);
      setMultispec(EMPTY_ZIP);
      rgbFileRef.current = null;
      msFileRef.current = null;
      gcpFileRef.current = null;
      logFileRef.current = null;
      setGcp(null);
      setGcpProgress(null);
      setGcpUploaded(false);
      setGcpSpeed(null);
      setGcpMeasured(false);
      setFlightLog(null);
      setLogProgress(null);
      setLogUploaded(false);
      setLogSpeed(null);
      setLogMeasured(false);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  }, [activeWorkspaceId, ensureDraft, flightLog, gcp, gcpUploaded, logUploaded, multispec, rgb]);

  const onCancel = async () => {
    if (!job) return;
    try {
      setJob(await cancelJob(job.id));
    } catch (err) {
      setError(apiError(err));
    }
  };

  const ownsFlight = (item: DroneJob) =>
    Boolean(
      workspace?.is_owner && (item.workspace_id == null || item.workspace_id === workspace.workspace_id)
    );

  const onDeleteFlight = async (item: DroneJob) => {
    if (!ownsFlight(item) || isActive(item.status) || deletingId) return;
    setDeletingId(item.id);
    setError(null);
    try {
      await deleteJob(item.id);
      setJobs((prev) => prev.filter((row) => row.id !== item.id));
      setVisibleIds((prev) => prev.filter((id) => id !== item.id));
      setExtents((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      if (detailId === item.id) setDetailId(null);
      if (job?.id === item.id) setJob(null);
      if (!activeWorkspaceId) return;
      const nextUsage = await fetchUsage(activeWorkspaceId);
      setWorkspace(nextUsage);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  const askDeleteFlight = (item: DroneJob) => {
    if (!ownsFlight(item) || isActive(item.status) || deletingId) return;
    const title = item.inputs.rgb?.filename || item.inputs.multispec?.filename || item.name;
    const files = [item.inputs.rgb, item.inputs.multispec].filter(Boolean);
    const linkedOnly = files.length > 0 && files.every((file) => file?.source === 's3_url');
    setPendingDelete({
      title: 'Delete this flight?',
      body: linkedOnly
        ? `“${title}” and its processed map will be deleted. This frees that space and analysis time. Photos that were only linked stay in their original location.`
        : `“${title}”, the uploaded photos, and the processed map will be deleted. This frees that space and analysis time on your account.`,
      confirmLabel: 'Delete',
      run: () => onDeleteFlight(item),
    });
  };

  useEffect(() => {
    if (!pendingDelete) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !confirmBusy) setPendingDelete(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmBusy, pendingDelete]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    let cancelled = false;
    fetchUsage(activeWorkspaceId)
      .then((next) => {
        if (!cancelled) setWorkspace(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [activeWorkspaceId, jobs.length, shareOpen, jobs.map((item) => `${item.id}:${item.status}`).join()]);

  useEffect(() => {
    if (pickedTab.current) return;
    if (!jobs.some((item) => item.status !== 'draft')) return;
    pickedTab.current = true;
    setStudioTab('flights');
  }, [jobs]);

  const running = isActive(job?.status);
  const history = jobs.filter((item) => item.status !== 'draft');
  const locked = busy || running;
  const rgbFilled = Boolean(rgb.file || (running && job?.inputs.rgb));
  const multispecFilled = Boolean(multispec.file || (running && job?.inputs.multispec));
  const queueBusy = history.some((item) => isActive(item.status));
  const photosFilled = rgbFilled || multispecFilled;
  const uploading =
    (rgb.file != null && !rgb.uploaded && rgb.progress != null) ||
    (multispec.file != null && !multispec.uploaded && multispec.progress != null) ||
    (gcp != null && !gcpUploaded && gcpProgress != null) ||
    (flightLog != null && !logUploaded && logProgress != null);
  const { isDark } = useTheme();
  const { user: authUser } = useAuth();
  const tone = panelTone(isDark);
  const canShare = !authUser?.is_system_guest && workspace?.is_owner !== false;

  const flightCount = history.length;
  const peopleCount = workspace?.people.length ?? 1;
  const storageGb = workspace ? (workspace.storage_used / (1024 * 1024 * 1024)).toFixed(1) : null;
  const limitGb = workspace ? (workspace.storage_limit / (1024 * 1024 * 1024)).toFixed(0) : null;
  const timeFull =
    workspace?.analysis_seconds_limit != null &&
    (workspace.analysis_seconds_used ?? 0) >= workspace.analysis_seconds_limit;
  const presetId = profile?.name === '16gb' ? 'fast' : profile?.name === '64gb' ? 'detailed' : 'balanced';
  const presetNote =
    presetId === 'fast'
      ? 'Low alignment. Dense cloud is skipped on this server.'
      : presetId === 'detailed'
        ? 'High alignment and a denser cloud.'
        : profile?.allow_dense
          ? 'Medium features with a light dense cloud.'
          : 'Quality is set by this server. Open the settings to see the values.';
  const stageLabel =
    STAGE_ROWS.find((row) => row.id === (job?.stage === 'dense_skipped' ? 'orthomosaic' : job?.stage))?.label ?? 'Processing';
  const saveWorkspaceName = async () => {
    const name = nameDraft.trim();
    if (!name || workspaceBusy) return;
    setWorkspaceBusy(true);
    setShareError(null);
    try {
      if (naming === 'rename' && activeWorkspaceId) {
        const next = await renameWorkspace(activeWorkspaceId, name);
        setWorkspaces((prev) => prev.map((item) => (item.workspace_id === next.workspace_id ? next : item)));
        setWorkspace((current) => (current ? { ...current, name: next.name } : current));
      } else {
        const next = await createWorkspace(name);
        setWorkspaces((prev) => [...prev, next]);
        setActiveWorkspaceId(next.workspace_id);
        setStudioTab('new');
      }
      setNaming(null);
      setNameDraft('');
      setWorkspaceMenu(false);
    } catch (err) {
      setShareError(apiError(err));
    } finally {
      setWorkspaceBusy(false);
    }
  };

  const workspaceInitials = (workspace?.name || 'WS')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div className={`relative flex h-full min-h-0 flex-col ${tone.fg}`}>
      <header className={`flex flex-col gap-2.5 border-b px-3.5 pt-3.5 pb-2.5 ${tone.line}`}>
        <div className="flex items-center gap-2.5">
          <div className="min-w-0 flex-1">
            <h2 className="text-[17px] leading-tight font-bold">Drone Studio</h2>
            <p className={`text-xs ${tone.muted}`}>Process flights, revisit results</p>
          </div>
        </div>
        {!workspacesReady ? (
          <p className={`m-0 text-xs ${tone.muted}`}>Loading workspaces…</p>
        ) : !activeWorkspaceId || naming === 'create' ? (
          <form
            className="flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void saveWorkspaceName();
            }}
          >
            <p className={`m-0 text-xs ${tone.muted}`}>
              {activeWorkspaceId ? 'Name the new workspace.' : 'Name a workspace before uploading a flight.'}
            </p>
            <input
              value={nameDraft}
              onChange={(event) => setNameDraft(event.target.value)}
              maxLength={80}
              placeholder="Workspace name"
              className={`w-full rounded-xl border px-3 py-2 text-sm outline-none ${tone.field}`}
              autoFocus
            />
            <div className="flex gap-2">
              <button type="submit" disabled={workspaceBusy || !nameDraft.trim()} className={`flex-1 rounded-xl py-2 text-sm font-semibold disabled:opacity-50 ${tone.brandBtn}`}>
                {workspaceBusy ? 'Saving…' : 'Create workspace'}
              </button>
              {activeWorkspaceId && (
                <button
                  type="button"
                  onClick={() => {
                    setNaming(null);
                    setNameDraft('');
                  }}
                  className={`px-3 text-xs font-semibold ${tone.ghost}`}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        ) : (
        <div className="flex flex-col gap-2">
        <div className={`flex items-center gap-2.5 rounded-xl border px-2.5 py-2 ${tone.card2}`}>
          <div className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg text-xs font-bold text-white ${tone.brandSolid}`}>
            {workspaceInitials || 'WS'}
          </div>
          <div className="min-w-0 flex-1">
            {naming === 'rename' ? (
              <form
                className="flex gap-1.5"
                onSubmit={(event) => {
                  event.preventDefault();
                  void saveWorkspaceName();
                }}
              >
                <input
                  value={nameDraft}
                  onChange={(event) => setNameDraft(event.target.value)}
                  maxLength={80}
                  className={`min-w-0 flex-1 rounded-lg border px-2 py-1 text-[12.5px] outline-none ${tone.field}`}
                  autoFocus
                />
                <button type="submit" disabled={workspaceBusy || !nameDraft.trim()} className={`text-xs font-semibold disabled:opacity-50 ${tone.brandText}`}>
                  Save
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setWorkspaceMenu((open) => !open)}
                className="block max-w-full truncate text-left text-[12.5px] font-semibold"
              >
                {workspace?.name || 'Workspace'}
              </button>
            )}
            <div className={`text-[11px] ${tone.muted}`}>
              {peopleCount} {peopleCount === 1 ? 'person' : 'people'} · {flightCount} {flightCount === 1 ? 'flight' : 'flights'}
              {workspace ? ` · ${workspace.runs_used} of ${workspace.runs_limit} runs` : ''}
              {storageGb && limitGb ? ` · ${storageGb} of ${limitGb} GB` : ''}
              {workspace?.analysis_seconds_limit != null
                ? ` · ${formatAnalysis(workspace.analysis_seconds_used ?? 0)} of ${formatAnalysis(workspace.analysis_seconds_limit)}`
                : ''}
            </div>
          </div>
          {naming !== 'rename' && (
            <div className="flex shrink-0 items-center gap-0.5">
              <button
                type="button"
                title="New workspace"
                aria-label="New workspace"
                onClick={() => {
                  setNameDraft('');
                  setNaming('create');
                  setWorkspaceMenu(false);
                }}
                className={`grid h-7 w-7 place-items-center rounded-lg ${tone.muted} hover:text-[#008540]`}
              >
                <i className="ri-add-line text-base" aria-hidden />
              </button>
              {workspace?.is_owner && (
                <button
                  type="button"
                  title="Rename workspace"
                  aria-label="Rename workspace"
                  onClick={() => {
                    setNameDraft(workspace.name);
                    setNaming('rename');
                    setWorkspaceMenu(false);
                  }}
                  className={`grid h-7 w-7 place-items-center rounded-lg ${tone.muted} hover:text-[#008540]`}
                >
                  <i className="ri-pencil-line text-base" aria-hidden />
                </button>
              )}
              {canShare && (
                <button
                  type="button"
                  title="Share workspace"
                  aria-label="Share workspace"
                  onClick={() => {
                    setShareError(null);
                    if (!activeWorkspaceId) return;
                    if (workspace?.is_owner) {
                      setShareOpen(true);
                      return;
                    }
                    fetchUsage(activeWorkspaceId)
                      .then((next) => {
                        setWorkspace(next);
                        if (next.is_owner) setShareOpen(true);
                        else setShareError('Only the workspace owner can share.');
                      })
                      .catch((err) => setShareError(apiError(err)));
                  }}
                  className={`grid h-7 w-7 place-items-center rounded-lg ${tone.muted} hover:text-[#008540]`}
                >
                  <i className="ri-share-forward-line text-base" aria-hidden />
                </button>
              )}
            </div>
          )}
        </div>
        {workspaceMenu && (
          <div className={`overflow-hidden rounded-xl border ${tone.card}`}>
            {workspaces.map((item) => (
              <button
                key={item.workspace_id}
                type="button"
                onClick={() => {
                  setActiveWorkspaceId(item.workspace_id);
                  setWorkspaceMenu(false);
                }}
                className={`block w-full truncate px-3 py-2 text-left text-[12.5px] ${
                  item.workspace_id === activeWorkspaceId ? 'font-semibold text-[#008540]' : ''
                }`}
              >
                {item.name}
                {item.is_owner ? '' : ' · shared'}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setNameDraft('');
                setNaming('create');
                setWorkspaceMenu(false);
              }}
              className={`block w-full border-t px-3 py-2 text-left text-[12.5px] font-semibold ${tone.line} ${tone.brandText}`}
            >
              New workspace
            </button>
          </div>
        )}
        </div>
        )}
        {shareError && <p className={`m-0 text-[12px] ${tone.danger}`}>{shareError}</p>}
        {activeWorkspaceId && naming !== 'create' && (
        <div className={`grid grid-cols-2 gap-1 text-[12.5px] font-medium ${tone.seg}`} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={studioTab === 'flights'}
            onClick={() => setStudioTab('flights')}
            className={`rounded-[9px] py-2 ${studioTab === 'flights' ? tone.segOn : tone.segOff}`}
          >
            Flights
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={studioTab === 'new'}
            onClick={() => setStudioTab('new')}
            className={`rounded-[9px] py-2 ${studioTab === 'new' ? tone.segOn : tone.segOff}`}
          >
            New flight
          </button>
        </div>
        )}
      </header>
      {activeWorkspaceId && naming !== 'create' && (
      <>
      <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3">
        {studioTab === 'new' && (
          <div className="flex flex-col gap-2.5">
            {queueBusy && (
              <p className={`m-0 rounded-xl px-3 py-2 text-[12.5px] leading-normal ${tone.soft} ${tone.warn}`}>
                A flight is already processing. Wait until it finishes before starting another. We will email you when the map is ready.
              </p>
            )}
            {timeFull && (
              <p className={`m-0 rounded-xl px-3 py-2 text-[12.5px] leading-normal ${tone.soft} ${tone.warn}`}>
                This account has used its drone analysis time for the month. Delete a finished flight to free that time, or ask an admin to raise the cap.
              </p>
            )}
            <p className={`m-0 text-xs font-medium ${tone.muted}`}>1 · Upload raw data</p>
            <ZipSlot
              title="RGB photos"
              hint="ZIP of JPG or TIFF"
              thumb="rgb"
              value={rgb}
              remote={running ? job?.inputs.rgb : null}
              onFile={(file) => stageZip('rgb', file)}
              onClear={() => askRemoveZip('rgb')}
              disabled={locked}
              tone={tone}
            />
            <ZipSlot
              title="Multispectral"
              hint="ZIP of JPG or TIFF"
              thumb="ms"
              value={multispec}
              remote={running ? job?.inputs.multispec : null}
              onFile={(file) => stageZip('multispec', file)}
              onClear={() => askRemoveZip('multispec')}
              disabled={locked}
              tone={tone}
            />
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={() => setExtras((prev) => ({ ...prev, gcp: !prev.gcp }))} className={extras.gcp ? tone.chipOn : tone.chip}>
                + GCP points
              </button>
              <button type="button" onClick={() => setExtras((prev) => ({ ...prev, log: !prev.log }))} className={extras.log ? tone.chipOn : tone.chip}>
                + Flight log
              </button>
            </div>
            {(extras.gcp || gcp) && (
              <FileSlot title="GCP points" accept=".txt,.csv" file={gcp} progress={gcpProgress} speedBps={gcpSpeed} measured={gcpMeasured} remoteName={running ? job?.inputs.gcp?.filename : null} onFile={(file) => (file ? stageSmall('gcp', file) : askRemoveSmall('gcp'))} disabled={locked} tone={tone} />
            )}
            {(extras.log || flightLog) && (
              <FileSlot title="Flight log" accept=".txt,.csv,.log" file={flightLog} progress={logProgress} speedBps={logSpeed} measured={logMeasured} remoteName={running ? job?.inputs.flight_log?.filename : null} onFile={(file) => (file ? stageSmall('flight_log', file) : askRemoveSmall('flight_log'))} disabled={locked} tone={tone} />
            )}

            <p className={`m-0 mt-1 text-xs font-medium ${tone.muted}`}>2 · Quality</p>
            <div className={`grid grid-cols-3 gap-1 ${tone.seg}`}>
              {(
                [
                  ['fast', 'Fast'],
                  ['balanced', 'Balanced'],
                  ['detailed', 'Detailed'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  disabled={id !== presetId}
                  title={id === presetId ? presetNote : 'This server uses one quality preset'}
                  className={`rounded-[9px] px-1 py-2 text-[12.5px] font-medium disabled:cursor-default ${
                    id === presetId ? tone.segOn : `${tone.segOff} opacity-60`
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className={`m-0 text-[11.5px] ${tone.muted}`}>{presetNote}</p>
            <button
              type="button"
              aria-expanded={advOpen}
              onClick={() => setAdvOpen((prev) => !prev)}
              className={`flex items-center justify-between px-3 py-2.5 text-[12.5px] font-medium ${tone.soft}`}
            >
              <span>Photogrammetry settings</span>
              <span className={tone.muted}>{advOpen ? 'Hide' : 'Edit'}</span>
            </button>
            {advOpen && (
              <div className={`flex flex-col px-3 ${tone.soft}`}>
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] items-center gap-x-3 gap-y-2 py-2">
                  <label htmlFor="drone-alignment" className={tone.muted}>Alignment quality</label>
                  <select
                    id="drone-alignment"
                    value={options.alignment_quality}
                    disabled={locked}
                    onChange={(event) =>
                      setOptions((prev) => ({ ...prev, alignment_quality: event.target.value as DroneOptions['alignment_quality'] }))
                    }
                    className={tone.select}
                  >
                    {(['low', 'medium', 'high'] as const).map((value) => (
                      <option key={value} value={value} disabled={!alignmentEnabled.includes(value)}>
                        {value[0].toUpperCase() + value.slice(1)}
                        {alignmentEnabled.includes(value) ? '' : ' — larger server'}
                      </option>
                    ))}
                  </select>
                  <span className={tone.muted}>Keypoint limit</span>
                  <span className={`text-right ${tone.muted}`}>Set by server</span>
                  <span className={tone.muted}>Dense cloud quality</span>
                  <span className={`text-right capitalize ${tone.muted}`}>{skipDense ? 'Skipped' : options.dense_quality}</span>
                  <span className={tone.muted}>Surface model</span>
                  <span className={`text-right ${tone.muted}`}>{profile?.allow_dtm ? 'DSM + DTM' : 'DSM'}</span>
                  <span className={tone.muted}>Coordinate system</span>
                  <span className="text-right">Auto UTM from GPS</span>
                </div>
              </div>
            )}

            <p className={`m-0 mt-1 text-xs font-medium ${tone.muted}`}>3 · Outputs</p>
            <div className="flex flex-wrap gap-1.5">
              {OUTPUTS.map((item) => {
                const enabled = profile ? profile.outputs_enabled.includes(item.id) : item.id === 'orthomosaic' || item.id === 'dsm';
                return (
                  <span
                    key={item.id}
                    title={enabled ? item.label : disabledNote}
                    className={enabled ? tone.chipOn : `${tone.chip} opacity-60`}
                  >
                    {item.label}
                  </span>
                );
              })}
            </div>
            {running && (
              <div className={`px-3 py-2.5 ${tone.soft}`}>
                <div className="flex items-center justify-between text-[12.5px] font-medium">
                  <span>{stageLabel}</span>
                  <span className={tone.brandText}>{job?.progress ?? 0}%</span>
                </div>
                <div className={`mt-2 h-1.5 overflow-hidden rounded-full ${tone.barTrack}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={job?.progress ?? 0}>
                  <i className={`block h-full rounded-full ${tone.progress}`} style={{ width: `${job?.progress ?? 0}%` }} />
                </div>
                <ul className="mt-2 flex list-none flex-col gap-2 p-0">
                  {STAGE_ROWS.map((row) => (
                    <StageRow key={row.id} label={row.label} state={stageState(row.id, job?.stage, job?.status, skipDense)} tone={tone} />
                  ))}
                </ul>
                <p className={`m-0 mt-2 text-[11.5px] ${tone.muted}`}>You can leave this page. We will email you when the map is ready.</p>
              </div>
            )}
            {error && <p className={`m-0 text-[12.5px] ${tone.danger}`}>{error}</p>}
            {job?.error && <p className={`m-0 text-[12.5px] ${tone.danger}`}>{job.error}</p>}
            {job?.warning && <p className={`m-0 text-[12.5px] ${tone.warn}`}>{job.warning}</p>}
            {!photosFilled && !running && !error && (!job || job.status === 'draft') && (
              <p className={`m-0 text-[12.5px] ${tone.danger}`}>Add an RGB or multispectral zip before processing can start.</p>
            )}
          </div>
        )}

        {studioTab === 'flights' && (
          <div
            className={`-mx-3.5 -my-3 flex min-h-full flex-col gap-3 px-3.5 py-3 ${isDark ? 'bg-[#0f1419]' : 'bg-[#EEF1EE]'}`}
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <div className="flex items-center justify-between px-0.5">
              <h3 className={`m-0 text-[13.5px] font-semibold ${isDark ? 'text-[#e6edf3]' : 'text-[#14201A]'}`}>Previous flights</h3>
              {visibleIds.length > 0 && (
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${isDark ? 'bg-[#1F5C36]/40 text-[#b7e4c7]' : 'bg-[#DCEEE2] text-[#1F5C36]'}`}>
                  {visibleIds.length} on map
                </span>
              )}
            </div>
            {error && <p className={`m-0 text-[12.5px] ${tone.danger}`}>{error}</p>}
            {history.length === 0 ? (
              <p className={`m-0 text-[12.5px] ${tone.muted}`}>No flights yet. Finished flights appear here.</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {history.map((item) => (
                  <HistoryRow
                    key={item.id}
                    item={item}
                    selected={detailId === item.id}
                    visible={visibleIds.includes(item.id)}
                    onSelect={() => selectJob(item)}
                    onToggle={() => toggleVisible(item.id)}
                    onZoom={() => zoomTo(item.id)}
                    onShare={() => setShareOpen(true)}
                    onRerun={() => setStudioTab('new')}
                    canDelete={ownsFlight(item)}
                    deleting={deletingId === item.id}
                    onDelete={() => askDeleteFlight(item)}
                    layerKind={item.id === job?.id ? layerKind : 'ortho'}
                    onLayerKind={(kind: DroneRaster, index) => {
                      setDetailId(item.id);
                      setJob(item);
                      setLayerKind(kind);
                      setLayerIndex(isDroneIndex(kind) ? index ?? null : null);
                    }}
                    cameras={
                      item.id === job?.id && item.outputs.shots_key && visibleIds.includes(item.id)
                        ? { on: showCameras, onToggle: () => setShowCameras((prev) => !prev) }
                        : null
                    }
                    dark={isDark}
                    tone={tone}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {(studioTab === 'new' || running) && (
      <div className={`border-t px-3.5 py-3 ${tone.line}`}>
        {running ? (
          <button
            type="button"
            onClick={() => void onCancel()}
            className={`w-full rounded-xl border py-2.5 text-sm font-semibold ${tone.danger} border-current bg-transparent`}
          >
            Cancel processing
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void onStart()}
            disabled={busy || !photosFilled || queueBusy || uploading || timeFull}
            className={`w-full rounded-xl py-2.5 text-sm font-semibold ${tone.brandBtn}`}
          >
            {uploading ? 'Uploading…' : busy ? 'Starting…' : job?.status === 'completed' ? 'Process again' : 'Process flight'}
          </button>
        )}
      </div>
      )}
      </>
      )}
      {shareOpen && workspace && (
        <ShareDialog
          workspace={workspace}
          tone={tone}
          onClose={() => setShareOpen(false)}
          onSent={(next) => setWorkspace(next)}
        />
      )}
      {pendingDelete && (
        <DeleteConfirm
          prompt={pendingDelete}
          busy={confirmBusy}
          tone={tone}
          onCancel={() => {
            if (!confirmBusy) setPendingDelete(null);
          }}
          onConfirm={() => {
            const prompt = pendingDelete;
            setConfirmBusy(true);
            void Promise.resolve(prompt.run()).finally(() => {
              setConfirmBusy(false);
              setPendingDelete(null);
            });
          }}
        />
      )}
    </div>
  );
}

function stageState(
  id: string,
  stage: string | undefined,
  status: string | undefined,
  skipDense: boolean
): 'done' | 'active' | 'skipped' | 'pending' | 'failed' {
  if (id === 'dense' && skipDense && status && status !== 'draft') return 'skipped';
  if (status === 'completed') return 'done';
  const order = ['upload', 'alignment', 'sparse', 'dense', 'orthomosaic', 'export'];
  let current = stage === 'dense_skipped' ? 'dense' : stage || 'upload';
  // With dense skipped, NodeODM spends that band texturing, which is the orthomosaic step.
  if (current === 'dense' && skipDense) current = 'orthomosaic';
  const currentIndex = order.indexOf(current);
  const rowIndex = order.indexOf(id);
  if (rowIndex < currentIndex) return 'done';
  if (rowIndex === currentIndex) {
    if (status === 'failed' || status === 'cancelled') return 'failed';
    if (status === 'preparing' || status === 'queued' || status === 'running') return 'active';
  }
  return 'pending';
}

interface PanelTone {
  fg: string;
  muted: string;
  line: string;
  card: string;
  card2: string;
  eta: string;
  barTrack: string;
  switchOff: string;
  select: string;
  check: string;
  summary: string;
  brandBtn: string;
  brandSolid: string;
  brandText: string;
  chip: string;
  chipOn: string;
  seg: string;
  segOn: string;
  segOff: string;
  ghost: string;
  field: string;
  iconBtn: string;
  soft: string;
  danger: string;
  warn: string;
  progress: string;
}

function panelTone(isDark: boolean): PanelTone {
  const selectIcon = isDark ? '%238b949e' : '%236b7280';
  return {
    fg: isDark ? 'text-[#e6edf3]' : 'text-[#111827]',
    muted: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    line: isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]',
    card: isDark ? 'border-[#2d3748] bg-[#1c2432]' : 'border-[#e5e7eb] bg-white',
    card2: isDark ? 'border-[#2d3748] bg-[#0d1117]' : 'border-[#e5e7eb] bg-[#f3f4f6]',
    eta: isDark ? 'bg-[#1c2432] text-[#8b949e]' : 'bg-[#f3f4f6] text-[#6b7280]',
    barTrack: isDark ? 'border-[#2d3748] bg-[#0d1117]' : 'border-[#e5e7eb] bg-[#f3f4f6]',
    switchOff: isDark ? 'bg-[#2d3748]' : 'bg-[#e5e7eb]',
    select: `w-full appearance-none rounded-[10px] border ${isDark ? 'border-[#2d3748] bg-[#161b22]' : 'border-[#e5e7eb] bg-white'} bg-[url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%278%27 fill=%27none%27 stroke=%27${selectIcon}%27 stroke-width=%272%27 stroke-linecap=%27round%27%3E%3Cpath d=%27M1 1.5l5 5 5-5%27/%3E%3C/svg%3E")] bg-[position:right_12px_center] bg-no-repeat px-3 py-[9px] pr-8 text-sm`,
    check: 'h-5 w-5 shrink-0 accent-[#008540]',
    summary: isDark
      ? 'border-[#2d3748] bg-[#161b22] text-[#e6edf3] shadow-[0_6px_24px_rgba(0,0,0,.45)]'
      : 'border-[#e5e7eb] bg-white text-[#111827] shadow-[0_6px_24px_rgba(17,24,39,.12)]',
    brandBtn: 'bg-[#008540] text-white hover:bg-[#006e36] disabled:cursor-not-allowed disabled:opacity-45',
    brandSolid: 'bg-[#008540] text-white',
    brandText: isDark ? 'text-[#34d399]' : 'text-[#008540]',
    chip: isDark
      ? 'rounded-full border border-[#2d3748] bg-[#1c2432] px-3 py-1.5 text-xs font-medium text-[#c9d1d9]'
      : 'rounded-full border border-[#e5e7eb] bg-white px-3 py-1.5 text-xs font-medium text-[#374151]',
    chipOn: isDark
      ? 'rounded-full border border-[#008540] bg-[rgba(0,133,64,.22)] px-3 py-1.5 text-xs font-medium text-[#34d399]'
      : 'rounded-full border border-[#008540] bg-[#00854014] px-3 py-1.5 text-xs font-medium text-[#008540]',
    seg: isDark
      ? 'rounded-xl border border-[#2d3748] bg-[#0d1117] p-1'
      : 'rounded-xl border border-[#e5e7eb] bg-[#f3f4f6] p-1',
    segOn: 'bg-[#008540] text-white',
    segOff: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    ghost: isDark
      ? 'rounded-xl border border-[#2d3748] bg-[#1c2432] text-[#e6edf3]'
      : 'rounded-xl border border-[#e5e7eb] bg-white text-[#111827]',
    field: isDark
      ? 'w-full rounded-[10px] border border-[#2d3748] bg-[#0d1117] px-3 py-2.5 text-[13px] text-[#e6edf3] outline-none placeholder:text-[#8b949e]'
      : 'w-full rounded-[10px] border border-[#e5e7eb] bg-white px-3 py-2.5 text-[13px] text-[#111827] outline-none placeholder:text-[#9ca3af]',
    iconBtn: isDark
      ? 'grid h-9 w-9 shrink-0 place-items-center rounded-[11px] border border-[#2d3748] bg-[#1c2432]'
      : 'grid h-9 w-9 shrink-0 place-items-center rounded-[11px] border border-[#e5e7eb] bg-white',
    soft: isDark ? 'rounded-xl bg-[#1c2432]' : 'rounded-xl bg-[#f3f4f6]',
    danger: 'text-[#c0392b]',
    warn: isDark ? 'text-[#fbbf24]' : 'text-[#8a5a00]',
    progress: 'bg-[#008540]',
  };
}

interface InviteChip {
  email: string;
  status: 'checking' | 'account' | 'new' | 'member' | 'unknown';
  name?: string | null;
}

function DeleteConfirm({
  prompt,
  busy,
  tone,
  onCancel,
  onConfirm,
}: {
  prompt: DeletePrompt;
  busy: boolean;
  tone: PanelTone;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/50" aria-label="Keep it" onClick={onCancel} disabled={busy} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-confirm-title"
        className={`relative z-10 w-full max-w-sm rounded-[14px] border p-4 shadow-xl ${tone.summary}`}
      >
        <h3 id="delete-confirm-title" className="text-base font-semibold">
          {prompt.title}
        </h3>
        <p className={`mt-2 text-sm leading-relaxed ${tone.muted}`}>{prompt.body}</p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={`flex-1 rounded-[10px] px-3 py-2 text-sm font-semibold disabled:opacity-50 ${tone.ghost}`}
          >
            Keep it
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 rounded-[10px] bg-[#c0392b] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy ? 'Deleting…' : prompt.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function chipNote(chip: InviteChip): string {
  if (chip.status === 'checking') return 'Checking Digisaka…';
  if (chip.status === 'account') return chip.name ? `${chip.name} · Digisaka account` : 'Has a Digisaka account';
  if (chip.status === 'new') return 'No Digisaka account yet. The invite will ask them to create one.';
  if (chip.status === 'member') return 'Already in this workspace';
  return 'Could not check this address';
}

function ShareDialog({
  workspace,
  tone,
  onClose,
  onSent,
}: {
  workspace: WorkspaceUsage;
  tone: PanelTone;
  onClose: () => void;
  onSent: (next: WorkspaceUsage) => void;
}) {
  const [draft, setDraft] = useState('');
  const [chips, setChips] = useState<InviteChip[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const rememberChip = (email: string) => {
    setChips((prev) => (prev.some((chip) => chip.email === email) ? prev : [...prev, { email, status: 'checking' }]));
    lookupWorkspaceAccount(workspace.workspace_id, email)
      .then((found) => {
        setChips((prev) =>
          prev.map((chip) =>
            chip.email === email
              ? {
                  email,
                  status: found.already_member ? 'member' : found.exists ? 'account' : 'new',
                  name: found.name,
                }
              : chip
          )
        );
      })
      .catch(() => {
        setChips((prev) => prev.map((chip) => (chip.email === email ? { ...chip, status: 'unknown' } : chip)));
      });
  };

  const addChip = (value: string) => {
    const email = value.trim().toLowerCase();
    if (!email.includes('@') || !email.includes('.')) return;
    setDraft('');
    rememberChip(email);
  };

  const send = async () => {
    const pending = draft.trim().toLowerCase();
    if (pending.includes('@') && !chips.some((chip) => chip.email === pending)) rememberChip(pending);
    const emails = chips.filter((chip) => chip.status !== 'member').map((chip) => chip.email);
    const pendingOk = pending.includes('@') && !emails.includes(pending) && !chips.some((chip) => chip.email === pending && chip.status === 'member');
    const ready = pendingOk ? [...emails, pending] : emails;
    if (!ready.length) {
      setError('That address is already in this workspace.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await inviteWorkspace(workspace.workspace_id, ready);
      const next = await fetchUsage(workspace.workspace_id);
      onSent(next);
      if (result.failed.length) {
        setError(`Could not email ${result.failed.join(', ')}`);
        setChips(result.failed.map((email) => ({ email, status: 'unknown' as const })));
        setDraft('');
      } else {
        onClose();
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="absolute inset-0 z-20 flex items-start justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-workspace-title"
        className={`mt-4 w-full rounded-2xl border p-5 shadow-xl ${tone.card}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 id="share-workspace-title" className="text-base font-semibold">
              Share “{workspace.name}”
            </h3>
            <p className={`mt-1 text-[12.5px] ${tone.muted}`}>
              Only the invited Gmail can join. We check whether that address already has a Digisaka account.
            </p>
          </div>
          <button type="button" onClick={onClose} className={`text-lg leading-none ${tone.muted}`} aria-label="Close">
            ×
          </button>
        </div>
        <div className={`mt-4 flex min-h-11 flex-wrap gap-1.5 rounded-xl border px-2 py-2 ${tone.line}`}>
          {chips.map((chip) => (
            <button
              key={chip.email}
              type="button"
              onClick={() => setChips((prev) => prev.filter((item) => item.email !== chip.email))}
              className={`rounded-full px-2 py-1 text-left text-xs ${tone.eta}`}
              title={chipNote(chip)}
            >
              {chip.email} · {chipNote(chip)} ×
            </button>
          ))}
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                addChip(draft);
              }
            }}
            placeholder="name@gmail.com"
            className="min-w-[140px] flex-1 bg-transparent px-1 text-sm outline-none"
          />
        </div>
        {error && <p className="mt-2 text-[12.5px] text-[#c0392b]">{error}</p>}
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy || (!chips.length && !draft.includes('@'))}
          className="mt-3 w-full rounded-full bg-[#008540] py-2.5 text-sm font-semibold text-white disabled:opacity-45"
        >
          {busy ? 'Sending…' : 'Send invite'}
        </button>
        <p className={`mt-4 text-[11px] font-semibold tracking-wide uppercase ${tone.muted}`}>People with access</p>
        <ul className="mt-2 flex list-none flex-col gap-2 p-0">
          {workspace.people.map((person) => (
            <li key={`${person.role}-${person.email}`} className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 truncate">{person.name || person.email}</span>
              <span className={`shrink-0 text-xs ${tone.muted}`}>{person.role === 'owner' ? 'Owner' : 'Upload & process'}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function filledZip(value: LocalZip, remote?: DroneFileRef | null): { name: string; sub: string } | null {
  if (value.file) {
    if (value.progress != null && value.progress < 1) {
      return { name: value.file.name, sub: `Uploading ${Math.round(value.progress * 100)}%` };
    }
    if (value.uploaded) return { name: value.file.name, sub: `${formatBytes(value.file.size)} · Uploaded` };
    return { name: value.file.name, sub: formatBytes(value.file.size) };
  }
  const url = value.url.trim();
  if (url) return { name: url.split('/').pop() || url, sub: 'Linked from S3' };
  if (remote?.filename || remote?.key) {
    return {
      name: remote.filename || remote.key?.split('/').pop() || 'Uploaded',
      sub: remote.bytes ? formatBytes(remote.bytes) : 'Uploaded',
    };
  }
  return null;
}

function UploadTip({ text, tone }: { text: string; tone: PanelTone }) {
  return (
    <p
      className={`relative m-0 flex items-start gap-1.5 rounded-[10px] bg-[#00854014] px-2.5 py-2 text-[11.5px] leading-snug ${tone.brandText}`}
      role="status"
      title={text}
    >
      <i className="ri-speed-up-line mt-px shrink-0 text-sm" aria-hidden />
      <span>{text}</span>
    </p>
  );
}

function UploadMark({ progress, done }: { progress: number | null; done: boolean }) {
  if (done) {
    return (
      <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-[#2f9e44] text-white" aria-label="Uploaded">
        <i className="ri-check-line text-sm" aria-hidden />
      </span>
    );
  }
  const percent = Math.round(Math.min(1, Math.max(0, progress ?? 0)) * 100);
  const amount = Math.max(0.16, Math.min(0.78, progress ?? 0));
  const radius = 11;
  const length = 2 * Math.PI * radius;
  return (
    <span className="relative grid h-8 w-8 shrink-0 place-items-center" aria-label={`Uploading ${percent}%`}>
      <svg className="h-8 w-8 animate-spin" viewBox="0 0 28 28" aria-hidden>
        <circle cx="14" cy="14" r={radius} fill="none" stroke="currentColor" strokeWidth="3" className="text-[#008540]/20" />
        <circle
          cx="14"
          cy="14"
          r={radius}
          fill="none"
          stroke="#008540"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${length * amount} ${length}`}
        />
      </svg>
      <span className="absolute text-[8px] font-semibold text-[#008540]">{percent}</span>
    </span>
  );
}

function ZipSlot({
  title,
  hint,
  thumb,
  value,
  remote,
  onFile,
  onClear,
  disabled,
  tone,
}: {
  title: string;
  hint: string;
  thumb: 'rgb' | 'ms';
  value: LocalZip;
  remote?: DroneFileRef | null;
  onFile: (file: File) => void;
  onClear: () => void;
  disabled: boolean;
  tone: PanelTone;
}) {
  const filled = filledZip(value, remote);
  const local = Boolean(value.file || value.url.trim());
  const spinning = Boolean(value.file) && !value.uploaded && value.progress != null;
  const alreadyUploaded = value.uploaded || (!value.file && !value.url.trim() && Boolean(remote?.filename || remote?.key));
  if (filled) {
    return (
      <div className={`flex flex-col gap-2.5 rounded-[14px] border p-3 ${tone.card}`}>
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="h-11 w-14 shrink-0 rounded-lg border"
            style={{
              borderColor: 'inherit',
              background:
                thumb === 'rgb'
                  ? 'linear-gradient(135deg,#5c7a3d 0 30%,#7b9a52 30% 55%,#43602c 55% 75%,#8c8a5a 75%)'
                  : 'linear-gradient(135deg,#3a3a3a 0 30%,#777 30% 55%,#2a2a2a 55% 75%,#9a9a9a 75%)',
            }}
          />
          <div className="min-w-0 flex-1">
            <b className="block truncate text-sm font-semibold" title={filled.name}>
              {filled.name}
            </b>
            <span className={`text-[12.5px] ${tone.muted}`}>{filled.sub}</span>
          </div>
          {(spinning || alreadyUploaded) && <UploadMark progress={value.progress} done={alreadyUploaded} />}
          {local && (
            <button
              type="button"
              onClick={onClear}
              disabled={disabled || (value.progress != null && value.progress < 1)}
              aria-label={`Remove ${filled.name}`}
              className={`grid h-[26px] w-[26px] place-items-center rounded-full ${tone.muted} disabled:opacity-40`}
            >
              <i className="ri-close-line" aria-hidden />
            </button>
          )}
        </div>
        {value.progress != null && value.progress < 1 && value.file && (
          <>
            <div className={`h-1.5 overflow-hidden rounded-full ${tone.barTrack}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(value.progress * 100)} aria-label={uploadTip(value.file.size, value.progress, value.speedBps, value.measured)}>
              <i
                className="relative block h-full overflow-hidden rounded-full bg-[#008540] transition-[width] duration-300"
                style={{ width: `${Math.max(8, Math.round(value.progress * 100))}%` }}
              >
                <span className="absolute inset-0 animate-pulse bg-white/30" />
              </i>
            </div>
            <UploadTip text={uploadTip(value.file.size, value.progress, value.speedBps, value.measured)} tone={tone} />
          </>
        )}
      </div>
    );
  }
  return (
    <div className={`flex flex-col gap-2.5 rounded-[14px] border p-3 ${tone.card}`}>
      <div className="flex min-w-0 items-center gap-3">
        <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-[10px] text-[#008540] ${tone.card2}`}>
          <i className="ri-image-line text-lg" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <b className="block truncate text-sm font-semibold">{title}</b>
          <span className={`text-[12.5px] ${tone.muted}`}>{hint}</span>
        </div>
        <label className={`shrink-0 rounded-full bg-[#e0f0e2] px-3 py-1 text-xs font-semibold text-[#008540] ${disabled ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}>
          File
          <input
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            disabled={disabled}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onFile(file);
            }}
          />
        </label>
      </div>
    </div>
  );
}

function FileSlot({
  title,
  accept,
  file,
  progress = null,
  speedBps = null,
  measured = false,
  remoteName,
  onFile,
  disabled,
  tone,
}: {
  title: string;
  accept: string;
  file: File | null;
  progress?: number | null;
  speedBps?: number | null;
  measured?: boolean;
  remoteName?: string | null;
  onFile: (file: File | null) => void;
  disabled: boolean;
  tone: PanelTone;
}) {
  const name = file?.name || remoteName;
  const spinning = Boolean(file) && progress != null && progress < 1;
  const alreadyUploaded = !spinning && (progress === 1 || (!file && Boolean(remoteName)));
  if (name) {
    return (
      <div className={`flex flex-col gap-2.5 rounded-[14px] border p-3.5 ${tone.card}`}>
        <div className="flex items-center gap-3">
        <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-[10px] text-[#008540] ${tone.card2}`}>
          <i className="ri-file-text-line text-lg" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <b className="block truncate text-sm font-semibold">{name}</b>
          <span className={`text-[12.5px] ${tone.muted}`}>
            {spinning ? 'Uploading…' : file ? (alreadyUploaded ? `${formatBytes(file.size)} · Uploaded` : formatBytes(file.size)) : 'Uploaded'}
          </span>
        </div>
        {(spinning || alreadyUploaded) && <UploadMark progress={progress} done={alreadyUploaded} />}
        {file && (
          <button
            type="button"
            disabled={disabled || spinning}
            aria-label={`Remove ${name}`}
            onClick={() => onFile(null)}
            className={`grid h-[26px] w-[26px] place-items-center rounded-full ${tone.muted} disabled:opacity-40`}
          >
            <i className="ri-close-line" aria-hidden />
          </button>
        )}
        </div>
        {spinning && file && (
          <>
            <div className={`h-1.5 overflow-hidden rounded-full ${tone.barTrack}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((progress ?? 0) * 100)} aria-label={uploadTip(file.size, progress ?? 0, speedBps, measured)}>
              <i
                className="relative block h-full overflow-hidden rounded-full bg-[#008540] transition-[width] duration-300"
                style={{ width: `${Math.max(8, Math.round((progress ?? 0) * 100))}%` }}
              >
                <span className="absolute inset-0 animate-pulse bg-white/30" />
              </i>
            </div>
            <UploadTip text={uploadTip(file.size, progress ?? 0, speedBps, measured)} tone={tone} />
          </>
        )}
      </div>
    );
  }
  return (
    <label className={`flex w-full cursor-pointer items-center gap-3 rounded-[14px] border p-3.5 text-left ${tone.card} ${disabled ? 'pointer-events-none opacity-60' : ''}`}>
      <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-[10px] text-[#008540] ${tone.card2}`}>
        <i className="ri-file-text-line text-lg" aria-hidden />
      </div>
      <b className="font-medium">{title}</b>
      <span className={`ml-auto shrink-0 text-[13px] ${tone.muted}`}>Optional</span>
      <input
        type="file"
        accept={accept}
        className="hidden"
        disabled={disabled}
        onChange={(event) => onFile(event.target.files?.[0] ?? null)}
      />
    </label>
  );
}

function StageRow({
  label,
  state,
  tone,
}: {
  label: string;
  state: 'done' | 'active' | 'skipped' | 'pending' | 'failed';
  tone: PanelTone;
}) {
  const right =
    state === 'done' ? 'Done' : state === 'active' ? 'Processing…' : state === 'skipped' ? 'Skipped' : state === 'failed' ? 'Stopped' : 'Pending';
  return (
    <li className={`flex items-center gap-2.5 text-[13.5px] ${state === 'active' ? 'font-semibold' : ''} ${state === 'pending' ? tone.muted : ''}`}>
      <span
        className={`grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full border ${
          state === 'done'
            ? 'border-[#2f9e44] bg-[#2f9e44] text-white'
            : state === 'active'
              ? 'animate-spin border-[2.5px] border-[#008540] border-t-transparent'
              : state === 'failed'
                ? 'border-[#c0392b]'
                : tone.line
        }`}
      >
        {state === 'done' && <i className="ri-check-line text-xs" aria-hidden />}
      </span>
      <span>{label}</span>
      <span className={`ml-auto text-[12.5px] font-normal ${state === 'active' ? 'font-medium text-[#008540]' : tone.muted}`}>{right}</span>
    </li>
  );
}

function HistoryRow({
  item,
  selected,
  visible,
  onSelect,
  onToggle,
  onZoom,
  onShare,
  onRerun,
  canDelete,
  deleting,
  onDelete,
  layerKind,
  onLayerKind,
  cameras,
  dark,
  tone,
}: {
  item: DroneJob;
  selected: boolean;
  visible: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onZoom: () => void;
  onShare: () => void;
  onRerun: () => void;
  canDelete: boolean;
  deleting: boolean;
  onDelete: () => void;
  layerKind: DroneRaster;
  onLayerKind: (kind: DroneRaster, index?: { a: number; b: number; colormap: string } | null) => void;
  cameras: { on: boolean; onToggle: () => void } | null;
  dark: boolean;
  tone: PanelTone;
}) {
  const rawTitle = item.inputs.rgb?.filename || item.inputs.multispec?.filename || item.name;
  const title = rawTitle.replace(/\.zip$/i, '');
  const when = item.created_at
    ? new Date(item.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';
  const completed = item.status === 'completed';
  const open = visible && completed;
  const stats = item.stats;
  const aligned = stats?.cameras_aligned;
  const images = stats?.images;
  const alignedPct = images && aligned != null ? Math.max(0, Math.min(100, Math.round((aligned / images) * 100))) : 0;
  const bands = bandSource(item, isDroneIndex(layerKind) ? 'ortho' : layerKind);
  const [indexes, setIndexes] = useState<ReturnType<typeof availableDroneIndexes>>([]);
  const [indexesReady, setIndexesReady] = useState(false);
  useEffect(() => {
    if (!open || !item.tiles.orthophoto || !item.outputs.orthophoto_key) {
      setIndexes([]);
      setIndexesReady(true);
      return;
    }
    let cancelled = false;
    setIndexesReady(false);
    loadBands({
      presigned: item.tiles.orthophoto,
      cacheKey: item.outputs.orthophoto_key,
      kind: 'ortho',
    })
      .then((info) => {
        if (cancelled) return;
        setIndexes(availableDroneIndexes(info.bands));
        setIndexesReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setIndexes([]);
        setIndexesReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, item.tiles.orthophoto, item.outputs.orthophoto_key]);
  useEffect(() => {
    if (indexesReady && isDroneIndex(layerKind) && !indexes.some((option) => option.key === layerKind)) {
      onLayerKind('ortho');
    }
  }, [indexesReady, indexes, layerKind, onLayerKind]);
  const ink = dark ? 'text-[#e6edf3]' : 'text-[#14201A]';
  const quiet = dark ? 'text-[#8b949e]' : 'text-[#5A6560]';
  const tile = dark ? 'bg-[#0d1117]' : 'bg-[#F5F7F5]';
  const hairline = dark ? 'border-[#2d3748]' : 'border-[#E3E8E4]';
  const statusLabel = completed ? 'Complete' : isActive(item.status) ? `${item.progress}%` : item.status === 'failed' ? 'Failed' : 'Cancelled';
  const statusClass = completed
    ? dark
      ? 'text-[#b7e4c7]'
      : 'text-[#1F5C36]'
    : item.status === 'failed'
      ? 'text-[#A82E1F]'
      : isActive(item.status)
        ? 'text-[#8a5a00]'
        : quiet;
  return (
    <li
      className={`flex flex-col rounded-2xl ${dark ? 'bg-[#1c2432]' : 'bg-white'} ${
        open
          ? 'gap-2 border-2 border-[#2F7D4B] p-2.5'
          : `border px-2.5 py-2 ${dark ? 'border-[#2d3748]' : 'border-[#D5DCD7]'}`
      }`}
    >
      <div className="flex items-center gap-1">
        <button type="button" onClick={onSelect} className="min-w-0 flex-1 text-left" aria-current={selected ? 'true' : undefined}>
          <span className={`block truncate text-[12.5px] font-semibold ${ink}`} title={rawTitle}>
            {title}
          </span>
          <span className={`mt-0.5 flex items-center gap-1 text-[11px] ${quiet}`}>
            <span className={`inline-flex items-center gap-1 font-semibold ${statusClass}`}>
              {completed && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              )}
              {statusLabel}
            </span>
            {when && (
              <>
                <span aria-hidden>·</span>
                <span>{when}</span>
              </>
            )}
          </span>
        </button>
        {open && (
          <button
            type="button"
            onClick={onZoom}
            aria-label={`Zoom to ${title}`}
            title="Zoom to flight"
            className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg ${dark ? 'text-[#c9d1d9]' : 'text-[#3F4A44]'}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
              <circle cx="12" cy="12" r="6.5" />
              <circle cx="12" cy="12" r="1.5" />
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
            </svg>
          </button>
        )}
        {completed && (
          <FlightSwitch
            on={visible}
            dark={dark}
            label={`${visible ? 'Hide' : 'Show'} ${title} on the map`}
            onClick={onToggle}
          />
        )}
      </div>
      {open && (
        <>
          <div className="grid grid-cols-3 gap-1.5">
            <FlightStat label="Images" tile={tile} quiet={quiet} ink={ink} value={images != null ? String(images) : '—'} />
            <FlightStat
              label="Area (est.)"
              tile={tile}
              quiet={quiet}
              ink={ink}
              value={stats?.area_ha != null ? String(stats.area_ha) : '—'}
              unit={stats?.area_ha != null ? 'ha' : undefined}
            />
            <FlightStat
              label="GSD (est.)"
              tile={tile}
              quiet={quiet}
              ink={ink}
              value={stats?.gsd_cm != null ? String(stats.gsd_cm) : '—'}
              unit={stats?.gsd_cm != null ? 'cm/px' : undefined}
            />
            <div className={`col-span-2 flex flex-col rounded-lg px-2 py-1.5 ${tile}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className={`text-[10px] font-medium ${quiet}`}>Cameras aligned</span>
                <span className={`text-[12px] font-semibold tabular-nums ${ink}`}>
                  {aligned != null ? `${aligned}${images != null ? ` / ${images}` : ''}` : '—'}
                </span>
              </div>
              <div className={`mt-1 h-1 overflow-hidden rounded-full ${dark ? 'bg-[#2d3748]' : 'bg-[#DCE3DE]'}`}>
                <div className="h-1 rounded-full bg-[#2F7D4B]" style={{ width: `${alignedPct}%` }} />
              </div>
            </div>
            <FlightStat
              label="Tie points"
              tile={tile}
              quiet={quiet}
              ink={stats?.tie_points != null ? ink : quiet}
              value={stats?.tie_points != null ? String(stats.tie_points) : '—'}
            />
          </div>
          {item.tiles.orthophoto && (
            <div className="flex flex-col gap-1">
              <span className={`text-[10px] font-semibold tracking-wide uppercase ${quiet}`}>Layer</span>
              <div className={`grid grid-cols-3 gap-1 rounded-lg p-0.5 ${dark ? 'bg-[#0d1117]' : 'bg-[#EEF1EE]'}`}>
                {(
                  [
                    ['ortho', 'Ortho', true],
                    ['dsm', 'DSM', Boolean(item.tiles.dsm)],
                    ['dtm', 'DTM', Boolean(item.tiles.dtm)],
                  ] as const
                ).map(([kind, label, enabled]) => (
                  <button
                    key={kind}
                    type="button"
                    aria-pressed={layerKind === kind}
                    onClick={() => onLayerKind(kind)}
                    disabled={!enabled}
                    className={`h-7 rounded-md text-[11.5px] font-medium disabled:cursor-not-allowed disabled:opacity-40 ${
                      layerKind === kind ? 'bg-[#2F7D4B] text-white' : dark ? 'text-[#c9d1d9]' : 'text-[#3F4A44]'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <span className={`mt-1 text-[10px] font-semibold tracking-wide uppercase ${quiet}`}>Index</span>
              <div className={`grid grid-cols-3 gap-1 rounded-lg p-0.5 ${dark ? 'bg-[#0d1117]' : 'bg-[#EEF1EE]'}`}>
                {(['ndvi', 'ndwi', 'ndmi'] as const).map((kind) => {
                  const option = indexes.find((entry) => entry.key === kind);
                  return (
                    <button
                      key={kind}
                      type="button"
                      aria-pressed={layerKind === kind}
                      onClick={() => option && onLayerKind(kind, option)}
                      disabled={!option}
                      title={option?.hint ?? droneIndexNeeds(kind)}
                      className={`h-7 rounded-md text-[11.5px] font-medium disabled:cursor-not-allowed disabled:opacity-40 ${
                        layerKind === kind ? 'bg-[#2F7D4B] text-white' : dark ? 'text-[#c9d1d9]' : 'text-[#3F4A44]'
                      }`}
                    >
                      {kind.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {(bands || cameras) && (
          <div className={`border-y ${hairline}`}>
            {bands && <BandInfo source={bands} tone={tone} compact />}
            {cameras && (
              <div className={`flex min-h-8 items-center justify-between gap-2 text-[12px] font-medium ${bands ? `border-t ${hairline}` : ''} ${ink}`}>
                <span className="inline-flex items-center gap-2.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#3B8FD9]" aria-hidden />
                  Camera positions
                </span>
                <FlightSwitch
                  on={cameras.on}
                  dark={dark}
                  label={`${cameras.on ? 'Hide' : 'Show'} camera positions`}
                  onClick={cameras.onToggle}
                />
              </div>
            )}
          </div>
          )}
          <button
              type="button"
              onClick={onShare}
              className={`flex items-center justify-center gap-1 rounded-lg border py-1.5 text-[12px] font-semibold ${
                dark ? 'border-[#2d3748] bg-[#1c2432] text-[#e6edf3]' : 'border-[#D5DCD7] bg-white text-[#14201A]'
              }`}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 15V3M7.5 7.5L12 3l4.5 4.5M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
              Share
            </button>
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="inline-flex items-center justify-center gap-1 self-center py-0.5 text-[12px] font-semibold text-[#A82E1F] disabled:opacity-50"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12M9 7V4h6v3" />
              </svg>
              {deleting ? 'Deleting…' : 'Delete flight and files'}
            </button>
          )}
        </>
      )}
      {!open && selected && item.status === 'failed' && (
        <div className={`mt-3 flex flex-col gap-2 border-t pt-3 ${hairline}`}>
          <p className={`m-0 text-xs leading-normal ${tone.warn}`}>{item.error || item.warning || 'This run could not finish.'}</p>
          <button type="button" onClick={onRerun} className={`py-2 text-xs font-semibold ${tone.ghost}`}>New flight</button>
        </div>
      )}
      {!open && selected && canDelete && !isActive(item.status) && (
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          className="mt-2 inline-flex items-center justify-center gap-1.5 self-center py-1.5 text-[12.5px] font-semibold text-[#A82E1F] disabled:opacity-50"
        >
          {deleting ? 'Deleting…' : 'Delete flight and files'}
        </button>
      )}
    </li>
  );
}

function FlightStat({
  label,
  value,
  unit,
  tile,
  quiet,
  ink,
}: {
  label: string;
  value: string;
  unit?: string;
  tile: string;
  quiet: string;
  ink: string;
}) {
  return (
    <div className={`flex flex-col rounded-lg px-2 py-1.5 ${tile}`}>
      <span className={`text-[10px] font-medium leading-tight ${quiet}`}>{label}</span>
      <span className={`text-[12px] font-semibold leading-tight tabular-nums ${ink}`}>
        {value}
        {unit && <small className={`ml-0.5 text-[10px] font-medium ${quiet}`}>{unit}</small>}
      </span>
    </div>
  );
}

function FlightSwitch({ on, dark, label, onClick }: { on: boolean; dark: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      className={`relative h-[22px] w-[38px] shrink-0 rounded-full ${on ? 'bg-[#008540]' : dark ? 'bg-[#3a4450]' : 'bg-[#CFD6D1]'}`}
    >
      <span className={`absolute top-[3px] left-[3px] h-4 w-4 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-4' : ''}`} />
    </button>
  );
}

const bandCache = new Map<string, CogBandInfo>();
const bandInflight = new Map<string, Promise<CogBandInfo>>();

function loadBands(source: BandSource): Promise<CogBandInfo> {
  const cached = bandCache.get(source.cacheKey);
  if (cached) return Promise.resolve(cached);
  const pending = bandInflight.get(source.cacheKey);
  if (pending) return pending;
  const request = fetch(infoUrl(source.presigned))
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
    .then((body: unknown) => {
      const parsed = parseCogInfo(body);
      if (!parsed) throw new Error('empty');
      bandCache.set(source.cacheKey, parsed);
      return parsed;
    })
    .finally(() => {
      bandInflight.delete(source.cacheKey);
    });
  bandInflight.set(source.cacheKey, request);
  return request;
}

function BandInfo({ source, tone, compact = false }: { source: BandSource; tone: PanelTone; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>(() =>
    bandCache.has(source.cacheKey) ? 'ready' : 'loading'
  );
  const [info, setInfo] = useState<CogBandInfo | null>(() => bandCache.get(source.cacheKey) ?? null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const cached = bandCache.get(source.cacheKey);
    if (cached) {
      setInfo(cached);
      setState('ready');
      return;
    }
    setState('loading');
    setInfo(null);
    loadBands(source)
      .then((parsed) => {
        if (!cancelled) {
          setInfo(parsed);
          setState('ready');
        }
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [open, source.cacheKey, source.presigned]);

  const bands =
    info?.bands
      .filter((band) => band.name !== 'Alpha')
      .map((band) =>
        (source.kind === 'dsm' || source.kind === 'dtm') &&
        info.count === 1 &&
        (band.name === 'Gray' || band.name.startsWith('Band '))
          ? { ...band, name: 'Elevation' }
          : band
      ) ?? [];
  const product = source.kind === 'dtm' ? 'DTM' : source.kind === 'dsm' ? 'DSM' : 'orthomosaic';

  return (
    <div className={compact ? '' : `border-t pt-2 ${tone.line}`}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex w-full items-center justify-between text-left font-medium ${
          compact ? 'min-h-8 text-[12px]' : 'text-[12.5px]'
        }`}
      >
        <span>Bands</span>
        <span className={`inline-flex items-center gap-1 ${tone.muted}`}>
          {open ? 'Hide' : 'Show'}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={open ? 'rotate-180' : ''}
            aria-hidden
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>
      {open && state === 'loading' && <p className={`m-0 mt-1 text-[12px] ${tone.muted}`}>Reading bands from the {product}…</p>}
      {open && state === 'error' && <p className="m-0 mt-1 text-[12px] text-[#c0392b]">Band information is unavailable.</p>}
      {open && state === 'ready' && info && (
        <>
          <ul className="m-0 mt-1 flex list-none flex-col gap-0.5 p-0">
            {bands.map((band) => (
              <li key={band.index} className="flex items-baseline justify-between gap-3 text-[12.5px]">
                <span className={tone.muted}>{band.index}</span>
                <span className="font-medium">{band.name}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
